import React, { useRef } from "react";
import "./ChartWorkspacePanel.css";

const DRAG_THRESHOLD_PX = 2;

function clampToRange(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}

function boundedPanelPosition(position, panelElement) {
  const x = Number(position?.x) || 0;
  const y = Number(position?.y) || 0;
  if (typeof window === "undefined") {
    return { x: Math.max(0, x), y: Math.max(0, y) };
  }

  const panelRect = panelElement?.getBoundingClientRect?.() || {};
  const workspaceElement = panelElement?.closest?.(".professionalChartWorkspace");
  const workspaceRect = workspaceElement?.getBoundingClientRect?.() || {};
  const originLeft = Number(workspaceRect.left) || 0;
  const originTop = Number(workspaceRect.top) || 0;
  const panelWidth = Math.max(0, Number(panelRect.width) || 0);
  const panelHeight = Math.max(0, Number(panelRect.height) || 0);
  const viewportWidth = Math.max(0, Number(window.innerWidth) || 0);
  const viewportHeight = Math.max(0, Number(window.innerHeight) || 0);

  const minimumX = Math.max(0, -originLeft);
  const minimumY = Math.max(0, -originTop);
  const maximumX = viewportWidth > 0
    ? Math.max(minimumX, viewportWidth - originLeft - panelWidth)
    : Number.POSITIVE_INFINITY;
  const maximumY = viewportHeight > 0
    ? Math.max(minimumY, viewportHeight - originTop - panelHeight)
    : Number.POSITIVE_INFINITY;

  return {
    x: clampToRange(x, minimumX, maximumX),
    y: clampToRange(y, minimumY, maximumY),
  };
}

function ChartWorkspacePanel({
  panel,
  children,
  onClose,
  onMove,
  onTogglePin,
  onFocus,
}) {
  const dragRef = useRef(null);

  if (!panel?.isOpen) return null;

  const handlePointerDown = (event) => {
    onFocus?.(panel.id);
    if (panel.isPinned) return;
    if (event.button !== undefined && event.button !== 0) return;

    dragRef.current = {
      pointerId: event.pointerId,
      originX: event.clientX,
      originY: event.clientY,
      startX: panel.position.x,
      startY: panel.position.y,
    };

    if (typeof event.currentTarget.setPointerCapture === "function") {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture improves drag continuity but is not required.
      }
    }
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || panel.isPinned) return;

    const deltaX = event.clientX - drag.originX;
    const deltaY = event.clientY - drag.originY;
    if (Math.abs(deltaX) < DRAG_THRESHOLD_PX && Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
      return;
    }

    const panelElement = event.currentTarget.closest?.(".chartWorkspacePanel") || null;
    onMove?.(
      panel.id,
      boundedPanelPosition(
        {
          x: drag.startX + deltaX,
          y: drag.startY + deltaY,
        },
        panelElement
      )
    );

    if (event.cancelable) event.preventDefault();
  };

  const endDrag = (event) => {
    if (
      dragRef.current &&
      typeof event.currentTarget.releasePointerCapture === "function"
    ) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (error) {
        // The browser may already have released this pointer.
      }
    }
    dragRef.current = null;
  };

  return (
    <section
      className={`chartWorkspacePanel${panel.isPinned ? " chartWorkspacePanelPinned" : ""}`}
      data-testid={`chart-workspace-panel-${panel.id}`}
      style={{
        transform: `translate(${panel.position.x}px, ${panel.position.y}px)`,
        zIndex: panel.zIndex,
      }}
      onPointerDown={() => onFocus?.(panel.id)}
      aria-label={`${panel.title} workspace panel`}
    >
      <header
        className="chartWorkspacePanelHeader"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        aria-label={`${panel.title} panel handle`}
      >
        <div>
          <span className="chartWorkspacePanelGrip" aria-hidden="true">⋮⋮</span>
          <strong>{panel.title}</strong>
        </div>
        <div className="chartWorkspacePanelActions">
          <button
            type="button"
            aria-label={panel.isPinned ? `Unpin ${panel.title}` : `Pin ${panel.title}`}
            aria-pressed={panel.isPinned}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => onTogglePin?.(panel.id)}
          >
            {panel.isPinned ? "Unpin" : "Pin"}
          </button>
          <button
            type="button"
            aria-label={`Close ${panel.title}`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => onClose?.(panel.id)}
          >
            ×
          </button>
        </div>
      </header>
      <div className="chartWorkspacePanelBody">{children}</div>
    </section>
  );
}

export default ChartWorkspacePanel;
